import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '@/shared/api';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  resource_type: string | null;
  resource_id: string | null;
  link: string | null;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'baru saja';
  if (m < 60) return `${m} mnt lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} jam lalu`;
  const d = Math.floor(h / 24);
  return `${d} hari lalu`;
}

const TYPE_ICON: Record<string, { Icon: any; color: string }> = {
  INGEST_DONE: { Icon: CheckCircle2, color: 'text-olive' },
  INGEST_FAILED: { Icon: AlertTriangle, color: 'text-crimson' },
};

export default function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: countData } = useQuery<{ count: number }>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api('/api/v1/notifications/unread-count'),
    refetchInterval: 15000,
  });
  const unread = countData?.count || 0;

  const { data: list, isLoading } = useQuery<Notification[]>({
    queryKey: ['notifications', 'list'],
    queryFn: () => api('/api/v1/notifications?limit=30'),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['notifications'] });
  };

  const openItem = async (n: Notification) => {
    if (!n.is_read) {
      try {
        await api(`/api/v1/notifications/${n.id}/read`, { method: 'POST' });
        invalidate();
      } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await api('/api/v1/notifications/read-all', { method: 'POST' });
      invalidate();
    } catch { /* ignore */ }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        className="icon-btn"
        title="Notifikasi"
        onClick={() => setOpen((o) => !o)}
      >
        <Bell />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-1 flex items-center justify-center bg-crimson text-paper font-mono text-[9px] font-bold leading-none rounded-full">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] w-[340px] max-h-[420px] flex flex-col bg-paper border border-ink-line shadow-lg z-50">
          <div className="flex items-center justify-between px-3 py-2 border-b border-ink-line">
            <span className="font-mono text-[10px] uppercase tracking-widest text-ink-2">
              Notifikasi {unread > 0 && <span className="text-crimson">· {unread} baru</span>}
            </span>
            <button
              onClick={markAll}
              disabled={unread === 0}
              className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-ink-3 hover:text-brass disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <CheckCheck className="w-3 h-3" />
              Tandai dibaca
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
            {isLoading && (
              <div className="flex items-center justify-center py-8 text-ink-3">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            )}
            {!isLoading && (!list || list.length === 0) && (
              <p className="font-mono text-[11px] text-ink-3 text-center py-8">
                Belum ada notifikasi.
              </p>
            )}
            {list?.map((n) => {
              const meta = TYPE_ICON[n.type] || { Icon: Bell, color: 'text-ink-3' };
              const Icon = meta.Icon;
              return (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={`w-full text-left flex gap-2.5 px-3 py-2.5 border-b border-ink-line/60 hover:bg-paper-2 transition ${
                    n.is_read ? '' : 'bg-brass-soft/30'
                  }`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[13px] leading-tight text-ink">
                      {n.title}
                    </div>
                    {n.body && (
                      <div className="font-mono text-[10px] text-ink-2 mt-0.5 leading-snug">
                        {n.body}
                      </div>
                    )}
                    <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mt-1">
                      {relativeTime(n.created_at)}
                    </div>
                  </div>
                  {!n.is_read && (
                    <span className="w-1.5 h-1.5 rounded-full bg-crimson mt-1.5 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
